/**
 * Master Workflow Orchestration Page
 *
 * Provides a UI for orchestrating interconnected business processes:
 * - Hire Employee (HR/Payroll)
 * - Client Sign Up (CRM/FHIR)
 * - Revenue Cycle Management (ERP/Claims)
 * - ADL Documentation (FHIR/Clinical)
 * - Payroll Service Rendering (Payroll/ERP)
 *
 * DRR-056: Data-Driven Workflow Configuration
 *
 * RBAC Scopes Required:
 * - workflow:read - View master workflows
 * - workflow:create - Start new master workflows
 * - instance:read - View workflow instances
 * - instance:write - Start sub-workflows
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Card,
  Group,
  Badge,
  Button,
  Select,
  Alert,
  Loader,
  Tabs,
  Paper,
  Drawer,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconPlayerPlay,
  IconList,
  IconAlertTriangle,
  IconHierarchy,
  IconRefresh,
  IconTopologyStarRing3,
} from '@tabler/icons-react';
import {
  MasterWorkflowDashboard,
  useProgramVariants,
  useReadyWorkflows,
  useInstance,
  type MnDhsProgram,
} from '@ferp/workflow-forms';
import { useAuth } from '@/auth/AuthContext';
import { useTenantId } from '@/auth';
import {
  ReactFlowWorkflow,
  type WorkflowStep,
  type StepStatus,
} from '../subgraphs/payroll/components/ReactFlowWorkflow';
import { GqlInstanceStatus } from '@/generated/graphql';

/** Business processes that compose the master workflow */
const BUSINESS_PROCESSES = [
  { id: 'hire-employee', name: 'Hire Employee', subgraphs: 'HR/Payroll', phase: 'Setup' },
  { id: 'client-signup', name: 'Client Sign Up', subgraphs: 'CRM/FHIR', phase: 'Setup' },
  { id: 'rcm', name: 'Revenue Cycle Management', subgraphs: 'ERP/Claims', phase: 'Financial' },
  { id: 'adl-documentation', name: 'ADL Documentation', subgraphs: 'FHIR/Clinical', phase: 'Service Delivery' },
  { id: 'payroll-rendering', name: 'Payroll Service Rendering', subgraphs: 'Payroll/ERP', phase: 'Financial' },
] as const;

/**
 * Master Workflow Page with program variant selection, instance management,
 * and react-flow visualization.
 */
export default function MasterWorkflowPage(): React.ReactElement {
  const { viewLayer } = useAuth();
  const tenantId = useTenantId() ?? 'default';
  const { repository: instanceRepo } = useInstance();
  const [selectedProgram, setSelectedProgram] = useState<MnDhsProgram | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [masterInstanceId, setMasterInstanceId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // Selected sub-workflow detail drawer
  const [detailOpened, { open: openDetail, close: closeDetail }] = useDisclosure(false);
  const [selectedSubWorkflow, setSelectedSubWorkflow] = useState<{
    workflowId: string;
    instanceId: string;
  } | null>(null);

  // Load available program variants from backend
  const { variants, loading: variantsLoading, error: variantsError } = useProgramVariants(tenantId);

  // Load ready workflows based on selected program
  const {
    readyWorkflows,
    loading: workflowsLoading,
  } = useReadyWorkflows({
    tenantId,
    programVariant: selectedProgram ?? '',
    completedWorkflows: [],
  });

  // Permission checks via view layer (admin/architect can manage workflows)
  const canStartWorkflow = viewLayer === 'admin' || viewLayer === 'architect';
  const canViewWorkflows = viewLayer === 'admin' || viewLayer === 'architect';

  const handleStartMasterWorkflow = useCallback(async () => {
    if (!selectedProgram) return;
    setStarting(true);
    setStartError(null);
    try {
      const instance = await instanceRepo.create(tenantId, {
        workflowDefinitionId: `master-${selectedProgram}`,
        inputData: { programVariant: selectedProgram },
        instanceName: `Master Workflow: ${selectedProgram}`,
        tags: ['master-workflow', selectedProgram],
        metadata: { programVariant: selectedProgram },
      });
      setMasterInstanceId(instance?.id ?? null);
      setActiveTab('dashboard');
    } catch (err) {
      setStartError(err instanceof Error ? err.message : 'Failed to start master workflow');
    } finally {
      setStarting(false);
    }
  }, [selectedProgram, instanceRepo, tenantId]);

  const handleProgramChange = useCallback((value: string | null) => {
    setSelectedProgram(value as MnDhsProgram | null);
    setMasterInstanceId(null);
  }, []);

  // Sub-workflow selection handler (wired to MasterWorkflowDashboard cards)
  const handleSubWorkflowSelect = useCallback(
    (workflowId: string, instanceId: string) => {
      setSelectedSubWorkflow({ workflowId, instanceId });
      openDetail();
    },
    [openDetail],
  );

  // Convert business processes to react-flow steps for visualization
  const flowSteps: WorkflowStep[] = useMemo(() => {
    return BUSINESS_PROCESSES.map((proc) => {
      // Match ready workflows from backend to determine status
      const readyWf = readyWorkflows.find(
        (rw) => rw.workflowName === proc.id || rw.id === proc.id,
      );
      let status: StepStatus = 'PENDING';
      if (readyWf) {
        const s = (readyWf as unknown as { status?: string }).status;
        if (s === GqlInstanceStatus.COMPLETED || s === 'Completed') status = 'COMPLETED';
        else if (s === GqlInstanceStatus.RUNNING || s === 'Running' || s === 'IN_PROGRESS') status = 'IN_PROGRESS';
        else if (s === 'REJECTED' || s === GqlInstanceStatus.FAILED) status = 'REJECTED'; // STRING-ENUM-EXCEPTION: no generated enum for 'REJECTED' in GqlInstanceStatus
      }
      return {
        id: proc.id,
        name: proc.name,
        description: `${proc.phase} - ${proc.subgraphs}`,
        status,
      };
    });
  }, [readyWorkflows]);

  const handleFlowStepClick = useCallback((stepId: string) => {
    const proc = BUSINESS_PROCESSES.find((p) => p.id === stepId);
    if (proc) {
      setSelectedSubWorkflow({ workflowId: stepId, instanceId: '' });
      openDetail();
    }
  }, [openDetail]);

  // Permission denied
  if (!canViewWorkflows) {
    return (
      <Container size="lg" py="xl">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Permission Denied"
          color="red"
        >
          You do not have permission to view master workflows.
          Required scope: workflow:read
        </Alert>
      </Container>
    );
  }

  // Loading state
  if (variantsLoading) {
    return (
      <Container size="lg" py="xl">
        <Stack ta="center" gap="md">
          <Loader size="lg" />
          <Text>Loading workflow configurations...</Text>
        </Stack>
      </Container>
    );
  }

  // Error state
  if (variantsError) {
    return (
      <Container size="lg" py="xl">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Error Loading Workflows"
          color="red"
        >
          {variantsError.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between" ta="left">
          <div>
            <Title order={1}>Master Workflow Orchestrator</Title>
            <Text c="dimmed" mt="xs">
              Orchestrate interconnected business processes across subgraphs
            </Text>
          </div>
          <Badge size="lg" variant="light" color="blue">
            DRR-056
          </Badge>
        </Group>

        {/* Program Selection Card */}
        <Card withBorder shadow="sm">
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text fw={500}>Select Program Variant</Text>
                <Text size="sm" c="dimmed">
                  Each program has specific workflow requirements
                </Text>
              </div>
              {masterInstanceId && (
                <Badge color="green" variant="light">
                  Active Instance: {masterInstanceId}
                </Badge>
              )}
            </Group>

            <Group>
              <Select
                label="MN DHS Program"
                placeholder="Select a program variant"
                value={selectedProgram}
                onChange={handleProgramChange}
                data={variants.map((v) => ({
                  value: v.variant,
                  label: v.name,
                }))}
                style={{ flex: 1, maxWidth: 400 }}
              />

              <Button
                leftSection={<IconPlayerPlay size={16} />}
                disabled={!selectedProgram || !canStartWorkflow || starting}
                loading={starting}
                onClick={handleStartMasterWorkflow}
                mt={24}
              >
                Start Master Workflow
              </Button>
            </Group>

            {selectedProgram && (
              <Paper p="sm" bg="blue.0" radius="sm">
                <Group gap="xs">
                  <IconHierarchy size={16} />
                  <Text size="sm">
                    Ready workflows: {workflowsLoading ? '...' : readyWorkflows.length}
                  </Text>
                </Group>
              </Paper>
            )}
          </Stack>
        </Card>

        {/* Start Error */}
        {startError && (
          <Alert icon={<IconAlertTriangle size={16} />} title="Failed to Start Workflow" color="red" variant="light" withCloseButton onClose={() => setStartError(null)}>
            {startError}
          </Alert>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconList size={14} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="flow" leftSection={<IconTopologyStarRing3 size={14} />}>
              Flow View
            </Tabs.Tab>
            <Tabs.Tab
              value="dashboard"
              leftSection={<IconHierarchy size={14} />}
              disabled={!masterInstanceId}
            >
              Active Dashboard
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconRefresh size={14} />}>
              History
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <Stack gap="md">
              <Text fw={500}>Supported Business Processes</Text>
              <Paper p="md" withBorder>
                <Stack gap="sm">
                  {BUSINESS_PROCESSES.map((process) => (
                    <Card
                      key={process.id}
                      withBorder
                      padding="sm"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleFlowStepClick(process.id)}
                    >
                      <Group justify="space-between">
                        <div>
                          <Text fw={500}>{process.name}</Text>
                          <Text size="xs" c="dimmed">{process.phase}</Text>
                        </div>
                        <Badge variant="outline" size="sm">
                          {process.subgraphs}
                        </Badge>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </Paper>

              <Alert color="blue" variant="light">
                Select a program variant above to see specific workflow requirements
                and start a master workflow instance.
              </Alert>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="flow" pt="md">
            <Card withBorder padding={0}>
              <ReactFlowWorkflow
                steps={flowSteps}
                onStepClick={handleFlowStepClick}
                height="600px"
                showMiniMap
              />
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="dashboard" pt="md">
            {masterInstanceId && selectedProgram ? (
              <MasterWorkflowDashboard
                masterInstanceId={masterInstanceId}
                programVariant={selectedProgram}
                onSubWorkflowSelect={handleSubWorkflowSelect}
                onStartSubWorkflow={(name) => {
                  // Auto-switch to dashboard when a sub-workflow is started
                  console.info(`Sub-workflow started: ${name}`);
                }}
              />
            ) : (
              <Alert color="yellow">
                Start a master workflow to view the dashboard.
              </Alert>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="history" pt="md">
            <Alert color="gray" variant="light">
              Workflow history coming soon. This will show completed and
              in-progress master workflow instances.
            </Alert>
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* Sub-Workflow Detail Drawer */}
      <Drawer
        opened={detailOpened}
        onClose={closeDetail}
        title="Sub-Workflow Details"
        position="right"
        size="lg"
      >
        {selectedSubWorkflow && (
          <Stack gap="md">
            <Card withBorder>
              <Stack gap="xs">
                <Text fw={600}>Workflow</Text>
                <Text>{selectedSubWorkflow.workflowId}</Text>
                {selectedSubWorkflow.instanceId && (
                  <>
                    <Text fw={600} mt="sm">Instance ID</Text>
                    <Text size="sm" c="dimmed">{selectedSubWorkflow.instanceId}</Text>
                  </>
                )}
              </Stack>
            </Card>
            <Alert color="blue" variant="light">
              Detailed sub-workflow view will show tasks, assignments, and progress
              once instances are running.
            </Alert>
          </Stack>
        )}
      </Drawer>
    </Container>
  );
}
